import { motion, useScroll, useTransform } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useRef } from 'react'

const Hero = () => {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollY } = useScroll()
  // Disable parallax on mobile to prevent scrolling issues
  const y = useTransform(scrollY, [0, 500], [0, window.innerWidth > 768 ? 250 : 0])

  return (
    <section className="relative">
      <div ref={heroRef} className="relative overflow-hidden min-h-[70vh] flex items-center justify-center touch-pan-y">
        {/* Background Video/Image with Parallax */}
        <motion.div
          style={{ y }}
          className="absolute inset-0 w-full h-full"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-accent/70 z-10" />
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/assets/video/ambulance_driving.mp4" type="video/mp4" />
          </video>
        </motion.div>

          {/* Content */}
          <div className="relative z-20 text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <motion.h1
                className="font-display font-bold mb-6 text-shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <span className="text-3xl lg:text-6xl block">Built to Serve</span>
                <span className="text-hero text-accent block">Those Who Serve.</span>
              </motion.h1>

              <motion.p
                className="text-lg lg:text-xl mb-8 max-w-2xl mx-auto text-gray-100"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Digital tools for Healthcare and EMS agencies that
                empower you to focus on what matters most... your patients.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <motion.a
                  href="#portfolio"
                  className="btn-accent group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault()
                    document.querySelector('#portfolio')?.scrollIntoView({
                      behavior: 'smooth'
                    })
                  }}
                >
                  <ChevronDown size={20} className="mr-2 group-hover:scale-110 transition-transform" />
                  View Our Work
                </motion.a>

                <motion.a
                  href="#contact"
                  className="btn-outline border-white text-white hover:bg-white hover:text-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault()
                    document.querySelector('#contact')?.scrollIntoView({
                      behavior: 'smooth'
                    })
                  }}
                >
                  Start a Project
                  <ChevronDown size={20} className="ml-2" />
                </motion.a>
              </motion.div>
            </motion.div>
          </div>

        {/* Gradient Fade Out - Soft Feathered Transition */}
        <div 
          className="absolute bottom-0 left-0 right-0 z-20 h-64 lg:h-80 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, #f9fafb 0%, rgba(249, 250, 251, 0.6) 20%, rgba(249, 250, 251, 0.4) 40%, rgba(249, 250, 251, 0.2) 60%, transparent 100%)'
          }}
        ></div>
      </div>
    </section>
  )
}

export default Hero
